import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type Venue } from '../api';
import { AppShell } from '../components/AppShell';

export function VenuesPage() {
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function load() {
    try {
      setErr(null);
      const data = await api.listVenues();
      setVenues(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Venues</h1>
            <p className="mt-1 text-muted">
              Where games are played. Each venue holds the displays that show
              the scoreboard, lobby signage, and ads.
            </p>
          </div>
          {!showForm && (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              + Add venue
            </button>
          )}
        </div>

        {showForm && (
          <AddVenueForm
            onDone={async () => {
              setShowForm(false);
              await load();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        {!venues && !err && <div className="text-muted">Loading…</div>}

        {venues && venues.length === 0 && !showForm && (
          <div className="card text-center">
            <div className="mx-auto mb-3 text-4xl">🏟️</div>
            <h3 className="text-lg font-semibold">No venues yet</h3>
            <p className="mt-1 text-sm text-muted">
              Add your first venue to start pairing displays and scheduling
              games.
            </p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => setShowForm(true)}
            >
              + Add your first venue
            </button>
          </div>
        )}

        {venues && venues.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {venues.map((v) => (
              <VenueCard key={v.id} venue={v} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function VenueCard({ venue }: { venue: Venue }) {
  return (
    <Link
      to={`/venues/${venue.id}`}
      className="card transition hover:border-brand/40 hover:bg-card/80"
    >
      <h3 className="text-lg font-semibold">{venue.name}</h3>
      {venue.address && (
        <p className="mt-1 text-sm text-muted">{venue.address}</p>
      )}
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">
            Capacity
          </dt>
          <dd className="mt-0.5 text-text">
            {venue.capacity != null ? venue.capacity.toLocaleString() : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">
            Displays
          </dt>
          <dd className="mt-0.5 text-text">
            {venue._count?.displays ?? 0}
          </dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs uppercase tracking-wider text-muted">
            Timezone
          </dt>
          <dd className="mt-0.5 font-mono text-xs text-text">{venue.timezone}</dd>
        </div>
      </dl>
    </Link>
  );
}

function AddVenueForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [capacity, setCapacity] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.createVenue({
        name,
        address: address || undefined,
        capacity: capacity ? Number(capacity) : undefined,
        timezone,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed_to_create');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h2 className="text-lg font-semibold">New venue</h2>
      <div>
        <label className="label">Name</label>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Main Gym, Field House, North Field"
          required
          minLength={2}
        />
      </div>
      <div>
        <label className="label">Address</label>
        <input
          className="input"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="123 Main St, Springfield"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="label">Capacity</label>
          <input
            className="input"
            type="number"
            min={0}
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            placeholder="e.g. 1200"
          />
        </div>
        <div>
          <label className="label">Timezone</label>
          <input
            className="input font-mono"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          />
        </div>
      </div>
      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Create venue'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
